import React, { useState } from 'react';
import { IoStatsChart, IoBriefcase, IoTrash, IoChevronForward, IoSearch, IoStar, IoHomeOutline, IoFileTrayStackedOutline, IoDocumentText, IoPeopleOutline, IoPieChart, IoArrowBack } from 'react-icons/io5';
import './reportscreen.css';
import { StandardSearch, highlightMatch } from '../styles/standardStyles';
// --- CORRECTED IMPORT PATH ---
// Changed from './components/stockrepo.js' to './StockRepo.js'
import { StockAndWastageReportsDetail } from './stockrepo.js';
import { useTranslate } from '../translations/useTranslate';
import BackToDashboardButton from './BackToDashboardButton';
import useBanquetFlag from '../hooks/useBanquetFlag';

// --- Data for the Main Reports Screen - will be translated dynamically ---
const getReportData = (t) => ({
  sales: [
    { id: 'sales-0', titleKey: "reports.sales.dailySummary.title", subtitleKey: "reports.sales.dailySummary.subtitle", screen: "sales-report", section: "sales-trend" },
    { id: 'sales-1', titleKey: "reports.sales.saleTrend.title", subtitleKey: "reports.sales.saleTrend.subtitle", screen: "sales-report", section: "gross-profit" },
    { id: 'sales-2', titleKey: "reports.sales.topItems.title", subtitleKey: "reports.sales.topItems.subtitle", screen: "sales-report", section: "top-items" },
  ],
  stock: [
    { id: 'stock-itemwise', titleKey: "reports.stock.itemWise.title", subtitleKey: "reports.stock.itemWise.subtitle" },
    { id: 'stock-performance', titleKey: "reports.stock.rawMaterial.title", subtitleKey: "reports.stock.rawMaterial.subtitle" },
    { id: 'stock-consumption', titleKey: "reports.stock.usage.title", subtitleKey: "reports.stock.usage.subtitle" },
  ],
  online: [
    { id: 'online-orders', titleKey: "reports.sales.onlineOrders.title", subtitleKey: "reports.sales.onlineOrders.subtitle", screen: "online-orders-report" },
  ],
  // Banquet. Literal copy, not translation keys, for the same reason the
  // previous cards use literals: these strings have no entry in the
  // translation files yet, and ReportCard falls back to title/subtitle exactly
  // so a new card cannot ship showing "reports.banquet.orders.title" to an owner.
  banquet: [
    {
      id: 'banquet-orders',
      title: 'Banquet Orders',
      subtitle: 'Functions billed per plate — plates sold, amount billed, and the dishes sent out',
      screen: 'banquet-orders-report',
    },
  ],
});

// --- Placeholder for Sales Reports Detail View ---
const SalesReportsDetailView = ({ onBack, selectedReportId }) => {
  const { t } = useTranslate();
  const reportData = getReportData(t);
  const report = reportData.sales.find(r => r.id === selectedReportId);
  return (
    <div className="screen-content">
      <div className="reports-sticky">
        <header className="reports-header">
          <button onClick={onBack} className="back-button"><IoArrowBack size={24} /></button>
          <h1>Sales Report Detail</h1>
        </header>
      </div>
      <main className="reports-list" style={{padding:'0.7rem'}}>
        <div className="report-card highlighted">
          <div className="card-text">
            <div className="card-title">{t(report?.titleKey)}</div>
            <div className="card-subtitle">{t(report?.subtitleKey)}</div>
            <p style={{ color: 'var(--highlight-color)', fontSize: '0.8rem', marginTop: '8px' }}>A detailed view for this sales report would be shown here.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

const ReportsScreen = ({ goTo, previousScreen }) => {
  const { t, language } = useTranslate();
  const isDevelopmentMode = process.env.NODE_ENV === 'development';
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState(() => {
    const savedView = sessionStorage.getItem('reportView');
    return savedView || 'list';
  });
  const [selectedReportId, setSelectedReportId] = useState(() => {
    const savedReportId = sessionStorage.getItem('selectedReportId');
    return savedReportId || null;
  });

  // Get translated report data
  const banquetEnabled = useBanquetFlag();
  const reportData = React.useMemo(() => {
    const data = getReportData(t);
    // Dropped rather than hidden at render: the search filter walks these
    // groups too, so leaving the card in the data would let it be found by
    // typing "banquet" on a business that does not have the lane.
    if (!banquetEnabled) delete data.banquet;
    return data;
  }, [t, banquetEnabled]);

  // Clear sessionStorage after reading
  React.useEffect(() => {
    if (view === 'details' && selectedReportId) {
      sessionStorage.removeItem('reportView');
      sessionStorage.removeItem('selectedReportId');
    }
  }, [view, selectedReportId]);

  const handleReportClick = (reportId) => {
    // Find the report data to check if it has a specific screen to navigate to
    const reportSection = Object.keys(reportData).find(section => 
      reportData[section].some(report => report.id === reportId)
    );
    
    if (reportSection) {
      const report = reportData[reportSection].find(r => r.id === reportId);
      
      // Check if this report should navigate to a specific screen
      if (report && report.screen) {
        // Navigate to the specified screen with the section info
        if (goTo) {
          if (report.id === 'online-orders' && !isDevelopmentMode) {
            goTo('online-orders-report', {
              featureName: t('reports.sales.onlineOrders.title') || 'Online Orders',
              backScreen: 'reports'
            });
            return;
          }
          if (report.section) {
            sessionStorage.setItem('highlightSection', report.section);
          }
          goTo(report.screen);
        }
        return;
      }
    }
    // Default behavior for reports without specific screens
    setSelectedReportId(reportId);
    setView('details');
  };

  const handleBack = () => {
    setView('list');
    setSelectedReportId(null);
  };

  const getFilteredReports = () => {
    if (!searchTerm) return reportData;
    const filtered = {};
    Object.keys(reportData).forEach((section) => {
      if (!searchTerm.trim()) {
        filtered[section] = reportData[section];
        return;
      }
      
      const searchLower = searchTerm.toLowerCase();
      
      // Get translated title and subtitle for filtering
      const getTranslatedText = (item) => ({
        title: String(item.title || t(item.titleKey) || '').toLowerCase(),
        subtitle: String(item.subtitle || t(item.subtitleKey) || '').toLowerCase()
      });
      
      // Priority 1: Items with title starting with search term
      const titleStartsWithMatches = reportData[section].filter(item => {
        const translated = getTranslatedText(item);
        return translated.title.startsWith(searchLower);
      });
      
      // Priority 2: Items with subtitle starting with search term (but title doesn't start with it)
      const subtitleStartsWithMatches = reportData[section].filter(item => {
        const translated = getTranslatedText(item);
        return !translated.title.startsWith(searchLower) &&
               translated.subtitle.startsWith(searchLower);
      });
      
      // Priority 3: Items containing search term in title (but not starting with it)
      const titleContainsMatches = reportData[section].filter(item => {
        const translated = getTranslatedText(item);
        return translated.title.includes(searchLower) &&
               !translated.title.startsWith(searchLower);
      });
      
      // Priority 4: Items containing search term in subtitle (but not starting with it)
      const subtitleContainsMatches = reportData[section].filter(item => {
        const translated = getTranslatedText(item);
        return !translated.title.includes(searchLower) &&
               translated.subtitle.includes(searchLower) &&
               !translated.subtitle.startsWith(searchLower);
      });
      
      const filteredItems = [
        ...titleStartsWithMatches,
        ...subtitleStartsWithMatches,
        ...titleContainsMatches,
        ...subtitleContainsMatches
      ];
      
      if (filteredItems.length > 0) {
        filtered[section] = filteredItems;
      }
    });
    return filtered;
  };

  const filteredReports = getFilteredReports();

  // A card may carry literal copy instead of translation keys. Reports added
  // before their strings reach the translation files would otherwise render the
  // key itself ("reports.kitchen.leakage.title") on the owner's screen.
  const ReportCard = ({ id, titleKey, subtitleKey, title, subtitle, onClick }) => {
    return (
      <div className="report-card" onClick={onClick}>
        <div className="card-text">
          <div className="card-title">{highlightMatch(title || t(titleKey), searchTerm)}</div>
          <div className="card-subtitle">{highlightMatch(subtitle || t(subtitleKey), searchTerm)}</div>
        </div>
        <IoChevronForward className="card-arrow-icon" />
      </div>
    );
  };

  const renderCurrentView = () => {
    if (view === 'details') {
      if (selectedReportId.startsWith('stock') || selectedReportId.startsWith('wastage')) {
        return <StockAndWastageReportsDetail onBack={handleBack} selectedReportId={selectedReportId} />;
      } else {
        return <SalesReportsDetailView onBack={handleBack} selectedReportId={selectedReportId} />;
      }
    }

    return (
      <div className="screen-content">
        <div className="reports-sticky">
          <header className="reports-header">
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <h1 style={{ margin:0, padding:'1rem 1rem 1rem 19.2px' }}>{t('reports.title') || 'Reports'}</h1>
            </div>
          </header>
        </div>
        <div className="reports-search">
          <StandardSearch
            style={{width:'100%'}}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.search')}
          />
        </div>
        <main className="reports-list" style={{padding:'0.7rem'}}>
          {Object.keys(filteredReports).length > 0 ? (
            <>
              {filteredReports.sales && (
                <section className="report-section">
                  <div className="section-title-header"><IoStatsChart className="section-icon" /><h3>{t('reports.sales.title')}</h3></div>
                  {filteredReports.sales.map((item) => (<ReportCard key={item.id} {...item} onClick={() => handleReportClick(item.id)} />))}
                </section>
              )}
              {filteredReports.stock && (
                <section className="report-section">
                  <div className="section-title-header"><IoBriefcase className="section-icon" /><h3>{t('reports.stock.title')}</h3></div>
                  {filteredReports.stock.map((item) => (<ReportCard key={item.id} {...item} onClick={() => handleReportClick(item.id)} />))}
                </section>
              )}

              {filteredReports.online && (
                <section className="report-section">
                  <div className="section-title-header"><IoDocumentText className="section-icon" /><h3>{t('reports.online.title')}</h3></div>
                  {filteredReports.online.map((item) => (<ReportCard key={item.id} {...item} onClick={() => handleReportClick(item.id)} />))}
                </section>
              )}

              {/* Banquet Reports — its own section, directly below Online Orders.
                  Rendered only when the business has the lane switched on; with the
                  flag off the Reports list is identical to what it was before. */}
              {filteredReports.banquet && (
                <section className="report-section">
                  <div className="section-title-header"><IoPeopleOutline className="section-icon" /><h3>Banquet Reports</h3></div>
                  {filteredReports.banquet.map((item) => (<ReportCard key={item.id} {...item} onClick={() => handleReportClick(item.id)} />))}
                </section>
              )}
            </>
          ) : (
            <div className="no-results"><p>{t('inventory.noItemsFound')} "{searchTerm}"</p></div>
          )}
        </main>
      </div>
    );
  };

  // Handle navigation from the report screen
  const handleNavigate = (screen) => {
    if (goTo) {
      goTo(screen);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="phone-container">
        {renderCurrentView()}
        {/* We're using the app's global FooterNav now, so no need for a footer here */}
      </div>
    </div>
  );
};

export default ReportsScreen;